import { GameBoardState, GameMode, HitStatus, Move, Player } from "./Types";
import {
  getAllPossibleMoveSets,
  LongNardyContext,
} from "./store/moves";
import { applyMoveToGameBoardState, pipCount } from "./store/gameBoardSlice";
import { LONG_NARDY_HEAD } from "./Constants";

/**
 * Улучшенная эвристика бота:
 *
 *  Короткие нарды (backgammon):
 *   - pip-дифференциал (гонка)
 *   - удары блотов соперника — жирный бонус
 *   - построение блоков в домашней зоне (prime)
 *   - наказание за собственные блоты (тем сильнее чем ближе к сопернику)
 *   - анкоры в тылу — защита
 *   - штраф за переполнение (>3 шашек в одной точке)
 *
 *  Длинные нарды:
 *   - pip-дифференциал
 *   - шашки в home — очень важно
 *   - построение блока (2-6 подряд) именно ВПЕРЕДИ шашек соперника —
 *     бонус пропорционален числу застрявших позади
 *   - уход с головы (свобода манёвра)
 *   - штраф за переполнение (>3 в одной точке) и за «мёртвые» стопки (>5)
 *   - бонус за диверсификацию (разные занятые точки в зоне игры)
 */

export function selectBotMoveSet(
  gameBoardState: GameBoardState,
  availableDice: number[],
  currentPlayer: Player,
  gameMode: GameMode,
  longNardyCtx?: LongNardyContext
): Move[] {
  if (availableDice.length === 0) return [];

  const allSets = getAllPossibleMoveSets(
    gameBoardState,
    availableDice,
    currentPlayer,
    gameMode,
    longNardyCtx
  );

  if (allSets.length === 0) return [];

  // Правило: максимум использованных костей
  const maxLen = allSets.reduce((m, s) => Math.max(m, s.length), 0);
  let candidates = allSets.filter((s) => s.length === maxLen);

  // Правило большего кубика при одном возможном шаге из двух
  if (maxLen === 1 && availableDice.length >= 2) {
    const maxDie = candidates.reduce((m, s) => Math.max(m, s[0].dieUsed), 0);
    const bigger = candidates.filter((s) => s[0].dieUsed === maxDie);
    if (bigger.length > 0) candidates = bigger;
  }

  // Ограничиваем количество кандидатов для больших наборов (куш 6-6 и т.п.)
  // — отсекаем по эвристически слабым ходам чтобы не висеть.
  const MAX_CANDIDATES = 500;
  if (candidates.length > MAX_CANDIDATES) {
    candidates = candidates.slice(0, MAX_CANDIDATES);
  }

  let bestScore = -Infinity;
  let bestSet = candidates[0];
  for (const moves of candidates) {
    let board = gameBoardState;
    for (const m of moves) {
      board = applyMoveToGameBoardState(board, m);
    }
    const score = evaluatePosition(board, currentPlayer, gameMode, moves);
    if (score > bestScore) {
      bestScore = score;
      bestSet = moves;
    }
  }
  return bestSet;
}

function evaluatePosition(
  board: GameBoardState,
  player: Player,
  mode: GameMode,
  moves: Move[]
): number {
  const opponent = player === Player.One ? Player.Two : Player.One;
  const myPips = pipCount(board, player);
  const oppPips = pipCount(board, opponent);

  let score = (oppPips - myPips) * 1.0;

  // Выброшенные шашки — основа победы
  score += board.homeState[player] * 40;
  score -= board.homeState[opponent] * 40;

  // На баре (только короткие)
  if (mode === GameMode.Backgammon) {
    score += board.barState[opponent] * 35;
    score -= board.barState[player] * 60;
  }

  // ── Распределение шашек: штраф за переполнение, бонус за диверсификацию ──
  let occupiedPoints = 0;
  let overstackPenalty = 0;
  for (let i = 0; i < 24; i++) {
    const my = board.pointsState[i][player];
    if (my > 0) occupiedPoints++;
    if (my > 3) {
      // Штраф растёт квадратично — 4 шашки норм, 6+ плохо
      overstackPenalty += (my - 3) * (my - 3) * 1.5;
    }
    // "Мёртвая" стопка — всё что больше 5 практически бесполезно
    if (my > 5) overstackPenalty += (my - 5) * 3;
  }
  score -= overstackPenalty;
  score += occupiedPoints * 1.2;

  if (mode === GameMode.Backgammon) {
    score += evaluateBackgammon(board, player, opponent, moves);
  } else {
    score += evaluateLongNardy(board, player, opponent);
  }

  return score;
}

function evaluateBackgammon(
  board: GameBoardState,
  player: Player,
  opponent: Player,
  moves: Move[]
): number {
  let score = 0;

  // Удары блотов противника — критично
  for (const m of moves) {
    if (m.hitStatus === HitStatus.IsHit) score += 25;
  }

  // Блоты и блоки
  for (let i = 0; i < 24; i++) {
    const my = board.pointsState[i][player];
    // Блот — уязвимость; тем выше риск, чем дальше от нашего дома
    if (my === 1) {
      const distFromOppHome =
        opponent === Player.One ? 24 - i : i + 1;
      // В зоне противника (0-5 для P1, 18-23 для P2) блоты особо опасны
      const risk = 24 - distFromOppHome; // 0..23, больше = опаснее
      score -= Math.max(0, risk) * 0.35;
    }
    if (my >= 2) {
      score += 3.5;
    }
  }

  // Прайм (подряд блоков) — гораздо ценнее отдельных блоков
  let consecutive = 0;
  for (let i = 0; i < 24; i++) {
    if (board.pointsState[i][player] >= 2) {
      consecutive++;
      if (consecutive >= 3) score += consecutive * 2.5; // 3,4,5,6 подряд
    } else {
      consecutive = 0;
    }
  }

  // Анкоры в зоне соперника (защитные точки)
  if (player === Player.One) {
    for (let i = 18; i <= 23; i++) {
      if (board.pointsState[i][player] >= 2) score += 5;
    }
  } else {
    for (let i = 0; i <= 5; i++) {
      if (board.pointsState[i][player] >= 2) score += 5;
    }
  }

  return score;
}

function evaluateLongNardy(
  board: GameBoardState,
  player: Player,
  opponent: Player
): number {
  let score = 0;

  const head = LONG_NARDY_HEAD[player];
  const onHead = board.pointsState[head][player];
  score -= onHead * 0.4; // лёгкий штраф за шашки на голове (нужно развиваться)

  // Построение блоков: 2+ подряд. Блок ценнее там где он перекрывает
  // шашки соперника.
  const oppHead = LONG_NARDY_HEAD[opponent];
  let consecutive = 0;
  let primeStart = -1;
  for (let i = 0; i < 24; i++) {
    if (board.pointsState[i][player] >= 2) {
      if (consecutive === 0) primeStart = i;
      consecutive++;
      score += 2; // базовый бонус за блок
      if (consecutive >= 3) score += consecutive * 2;
      if (consecutive >= 6) score += 15; // идеальная стена
    } else {
      if (consecutive >= 2 && primeStart >= 0) {
        // Оценим сколько шашек соперника застряло "позади" этого блока.
        // Позади = ближе к голове соперника по направлению движения.
        score += stuckBehindBlock(board, opponent, primeStart, i - 1, oppHead);
      }
      consecutive = 0;
      primeStart = -1;
    }
  }
  if (consecutive >= 2 && primeStart >= 0) {
    score += stuckBehindBlock(board, opponent, primeStart, 23, oppHead);
  }

  return score;
}

/**
 * Сколько шашек opponent'а находится "до" блока [blockStart..blockEnd]
 * по своему направлению движения. Чем больше — тем ценнее блок.
 */
function stuckBehindBlock(
  board: GameBoardState,
  opponent: Player,
  blockStart: number,
  blockEnd: number,
  oppHead: number
): number {
  let stuck = 0;
  // Opponent движется от oppHead в направлении +1 (все в длинных идут
  // против часовой). "Позади блока" = на дистанции [0..blockStart-oppHead)
  // от головы соперника, т.е. ещё не дошёл до блока.
  for (let i = 0; i < 24; i++) {
    const cnt = board.pointsState[i][opponent];
    if (cnt <= 0) continue;
    const distFromOppHead = (i - oppHead + 24) % 24;
    const blockStartFromOppHead = (blockStart - oppHead + 24) % 24;
    if (distFromOppHead < blockStartFromOppHead) {
      stuck += cnt;
    }
  }
  return stuck * 1.8;
}
